import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { KLARNA_ENABLED } from "../config";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Lock, CheckCircle2, PlayCircle, ArrowLeft, Loader2, Layers, Infinity as InfinityIcon, RefreshCw, Eye, ChevronDown, Clock } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);
  const { user, login } = useAuth();
  const { t, lang, languages } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/courses/${slug}?lang=${lang}`).then((r) => setCourse(r.data)).catch(() => navigate("/"));
  }, [slug, user, lang, navigate]);

  const buy = async () => {
    if (!user) { login(); return; }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: course.lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("payment.checkoutError"));
      setLoading(false);
    }
  };

  if (!course) {
    return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="animate-spin text-[#F5842B]" /></div>;
  }

  const accent = course.accent;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <Navbar />
      <section className="relative pt-16">
        <div className="relative h-[380px] overflow-hidden">
          <img src={course.cover} alt={course.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(11,14,20,0.5) 0%, rgba(11,14,20,0.85) 60%, #0B0E14 100%)` }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 20% 30%, ${accent}22, transparent 55%)` }} />
          <div className="absolute bottom-0 inset-x-0">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-8">
              <Link to="/#corsi" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-5 transition-colors" data-testid="course-back-btn">
                <ArrowLeft size={16} /> {t("course.back")}
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-xs tracking-[0.25em] px-3 py-1.5 rounded-full font-semibold" style={{ backgroundColor: `${accent}22`, color: accent, border: `1px solid ${accent}55` }}>{course.code}</span>
                {course.owned && <span className="font-mono text-xs px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 flex items-center gap-1"><CheckCircle2 size={13} /> {t("course.bought")}</span>}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{course.title}</h1>
              <p className="text-lg mt-2 font-medium" style={{ color: accent }}>{course.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="font-mono text-xs uppercase tracking-[0.3em] mb-4" style={{ color: accent }}>{course.tagline}</p>
          <p className="text-lg text-slate-300 leading-relaxed">{course.description}</p>

          <h2 className="font-display text-2xl font-bold mt-12 mb-6">{t("course.program")}</h2>
          <div className="space-y-3">
            {course.topics.map((tp, i) => {
              const isPreview = i === 0 && !course.owned;
              const isLockedRow = i > 0 && !course.owned;
              return (
                <div key={i} data-testid={`course-module-${i}`}>
                  <div
                    onClick={isPreview ? () => setPreviewOpen((o) => !o) : undefined}
                    className={`glass rounded-xl p-4 flex items-center gap-4 transition-colors ${isPreview ? "cursor-pointer hover:border-white/20" : ""} ${isLockedRow ? "opacity-70" : ""}`}
                    style={isPreview ? { borderColor: `${accent}55` } : {}}
                    data-testid={isPreview ? "course-preview-toggle" : undefined}
                  >
                    <span className="font-mono text-sm h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}22`, color: accent }}>{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-slate-200 flex-1">{tp}</span>
                    {isPreview && (
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 flex items-center gap-1"><Eye size={11} /> {t("course.freePreview")}</span>
                        <ChevronDown size={16} className={`text-slate-400 transition-transform ${previewOpen ? "rotate-180" : ""}`} />
                      </span>
                    )}
                    {isLockedRow && <Lock size={15} className="text-slate-500 shrink-0" />}
                  </div>
                  {isPreview && previewOpen && (
                    <div className="mt-2 rounded-xl p-5 border border-white/10 bg-[#0e131c] animate-float-up" data-testid="course-preview-panel">
                      <p className="text-sm text-slate-300 leading-relaxed">{course.description}</p>
                      <p className="text-xs text-slate-500 mt-4 flex items-center gap-2"><Lock size={13} /> {t("course.previewHint")}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[[Layers, t("course.modulesLabel", { n: course.topics.length }), t("course.onDemand")], [InfinityIcon, t("course.lifetime"), t("course.lifetimeDesc")], [RefreshCw, t("course.updates"), t("course.updatesDesc")]].map(([Icon, a, b], i) => (
              <div key={i} className="glass rounded-xl p-5">
                <Icon size={20} style={{ color: accent }} />
                <div className="font-display font-semibold mt-3">{a}</div>
                <div className="text-xs text-slate-400 mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 glass rounded-2xl p-7 border" style={{ borderColor: `${accent}44` }} data-testid="course-buy-card">
            <div className="mb-5 pb-5 border-b border-white/10" data-testid="course-available-langs">
              <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">{t("course.availableIn")}</span>
              {course.available_langs && course.available_langs.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {course.available_langs.map((lg) => {
                    const L = languages.find((x) => x.code === lg);
                    return (
                      <span key={lg} className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-400/30">
                        <span>{L?.flag}</span> {L?.short || lg.toUpperCase()}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 mt-2">
                  <Clock size={13} /> {t("course.comingSoonLang")}
                </div>
              )}
            </div>
            {course.owned ? (
              <>
                <div className="flex items-center gap-2 text-emerald-300 font-semibold"><CheckCircle2 size={18} /> {t("course.active")}</div>
                <div className="mt-5">
                  <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${course.progress_percent}%`, backgroundColor: accent }} />
                  </div>
                  <span className="text-xs text-slate-400 font-mono mt-2 block">{course.progress_percent}% {t("course.completedPct")}</span>
                </div>
                {course.locked ? (
                  <div className="mt-6 text-sm text-slate-400 flex items-start gap-2">
                    <Lock size={16} className="mt-0.5 shrink-0" />
                    {t("course.locked", { course: course.unlock_after_title })}
                  </div>
                ) : (
                  <button onClick={() => navigate(`/learn/${course.slug}`)} className="mt-6 w-full text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity" style={{ backgroundColor: accent }} data-testid="course-continue-btn">
                    <PlayCircle size={18} /> {t("course.goTo")}
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="text-sm text-slate-400">{t("course.priceLabel")}</span>
                <div className="font-display text-5xl font-extrabold mt-1">€{(course.price_cents / 100).toFixed(0)}</div>
                {KLARNA_ENABLED && (
                  <div className="flex items-center gap-2 mt-2" data-testid="course-installment">
                    <img src="/klarna.svg" alt="Klarna" className="h-4 w-auto" />
                    <span className="text-xs text-slate-300">{t("card.installment", { amount: (course.price_cents / 300).toFixed(2).replace(".", ",") })}</span>
                  </div>
                )}
                <ul className="mt-6 space-y-3 text-sm text-slate-300">
                  {t("course.feats").map((f) => (
                    <li key={f} className="flex items-center gap-2"><CheckCircle2 size={16} style={{ color: accent }} /> {f}</li>
                  ))}
                </ul>
                <button onClick={buy} disabled={loading} className="mt-7 w-full adj-gradient text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60" data-testid="course-buy-btn">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : t("course.buy")}
                </button>
                {!user && <p className="text-xs text-slate-500 text-center mt-3">{t("course.loginHint")}</p>}
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
